import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { allowedShareAccessLevels, isSessionSharingDisabled } from '@reflex/shared';
import { Button } from '@reflex/ui/components/ui/button';
import { asOrgId } from '@reflex/ui/client/api/org-id';
import {
  useApproveShareRequestMutation,
  useDeclineShareRequestMutation,
  useShareRequests,
  type ShareRequest,
} from '@reflex/ui/client/api/share-requests';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useSessionSharingPolicy } from '@/hooks/auth/useSessionSharingPolicy';
import { useUsers } from '@/hooks/auth/useUsers';
import { useSocketMessage } from '@/hooks/socket/useSocket';
import type { Agent, WsServerMessage } from '@/lib/types';

export interface ShareRequestsBannerProps {
  agent: Agent;
}

/**
 * Pending share requests on the open run, surfaced to whoever can resolve
 * them (the owner, or an org admin). Approving opens a view grant to the
 * requester — the same grant the Share dialog writes — and the requester's
 * open tab flips to the run over WS. Renders nothing when there is no
 * pending ask the caller can act on.
 */
export function ShareRequestsBanner({ agent }: ShareRequestsBannerProps) {
  const { activeOrganizationId } = useOrganization();
  const orgId = asOrgId(activeOrganizationId ?? 'global');
  const queryClient = useQueryClient();
  const policy = useSessionSharingPolicy();
  const approvalAccess = allowedShareAccessLevels(policy)[0] ?? 'view';
  // With sharing off entirely no approval can succeed; the Share button is
  // hidden so the owner doesn't click into a 403. Decline still works to
  // clean up requests that were filed before the policy narrowed.
  const sharingDisabled = isSessionSharingDisabled(policy);
  // The approval level isn't always 'view' — in an edit-only org the least
  // privilege the org can confer is 'edit'. Name it so the owner knows what
  // they're granting before clicking.
  const approvalLabel = approvalAccess === 'view' ? 'Share' : 'Share (edit)';
  const { data } = useShareRequests(orgId, {
    resourceType: 'agent',
    resourceId: agent.id,
    status: 'pending',
  });
  const { data: users = [] } = useUsers();
  const approve = useApproveShareRequestMutation(orgId);
  const decline = useDeclineShareRequestMutation(orgId);
  // Which row's resolution is in flight, so resolving one ask does not
  // lock the buttons of every other pending ask in the banner.
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type === 'share-request:created' && msg.resourceId === agent.id) {
          void queryClient.invalidateQueries({ queryKey: ['share-requests', orgId] });
        }
      },
      [agent.id, orgId, queryClient],
    ),
  );

  const actionable = useMemo(
    () => (data?.requests ?? []).filter((r) => r.canResolve),
    [data?.requests],
  );

  const requesterName = useCallback(
    (request: ShareRequest) => {
      const requester = users.find((u) => u.id === request.requesterUserId);
      return requester?.name ?? requester?.email ?? 'A teammate';
    },
    [users],
  );

  if (actionable.length === 0) return null;

  async function handleApprove(request: ShareRequest) {
    setResolvingId(request.id);
    try {
      await approve.mutateAsync({ requestId: request.id, data: { access: approvalAccess } });
      toast.success(`Run shared with ${requesterName(request)}`);
    } catch (err) {
      toast.error('Failed to share', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setResolvingId(null);
    }
  }

  async function handleDecline(request: ShareRequest) {
    setResolvingId(request.id);
    try {
      await decline.mutateAsync({ requestId: request.id });
      toast.success('Request declined');
    } catch (err) {
      toast.error('Failed to decline', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <div className="border-b border-border bg-muted/40" data-testid="share-requests-banner">
      {actionable.map((request) => (
        <div key={request.id} className="flex items-center gap-3 px-4 py-2">
          <UserPlus className="icon-sm shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1 text-sm">
            <span className="font-medium">{requesterName(request)}</span>
            <span className="text-muted-foreground"> asked for access to this run</span>
            {request.message && (
              <span className="block truncate text-xs text-muted-foreground">
                “{request.message}”
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!sharingDisabled && (
              <Button
                size="sm"
                onClick={() => void handleApprove(request)}
                disabled={resolvingId === request.id}
              >
                {approvalLabel}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void handleDecline(request)}
              disabled={resolvingId === request.id}
            >
              Decline
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
